<template>
  <div
    class="search-filter-input"
    :class="cls"
  >
    <span
      v-if="label"
      :class="'search-title' + labelCls"
    >{{ label }}</span>
    <div
      v-if="curInputType === INPUT_TYPE.INPUT"
      :class="['input-area', 'search-area', cls, disabled ? 'disabled-element': null]"
    >
      <HKLInput
        type="text"
        :value="value || ''"
        :disabled="disabled"
        @input="returnMessage($event.target.value)"
        @keyup.delete="returnMessage($event.target.value)"
        @keydown.enter="_enterEvent(($event.target.value))"
      />
    </div>
    <div
      v-else-if="curInputType === INPUT_TYPE.SELECT"
      :class="['select-area', 'search-area', cls]"
    >
      <HKLMultiSelect
        v-if="multiple"
        :value="hklSelectValue"
        :options="hklSelectOptions"
        :disabled="!options.length || disabled"
        :showSearch="selectSearchable"
        placeholder=""
        @change="onHklSelectChange"
      />
      <HKLSelect
        v-else
        :value="hklSelectValue"
        :options="hklSelectOptions"
        :disabled="!options.length || disabled"
        :showSearch="selectSearchable"
        placeholder=""
        @change="onHklSelectChange"
      />
    </div>
    <div
      v-else-if="curInputType === INPUT_TYPE.DATE"
      :class="['date-area', 'search-area', cls]"
    >
      <DatePicker
        :class="['columns', {'singleDatePicker': singleDatePicker}]"
        :calendarButton="true"
        calendarButtonIcon="fa fa-calendar"
        :value="startDay"
        :placeholder="dateFromPlaceholder"
        :clearButton="clearButton"
        :disabled="!disabledDateRangeBySelect ? (disabledDate ? startDisabledDatesUntil : undefined) : startDisableBySelectedEndDate"
        :disabledPicker="disabled"
        :canPutTotop="datePickerCanPutTotop"
        @selected="updateStartDate"
      />
      <DatePicker
        v-if="!singleDatePicker"
        class="columns"
        :calendarButton="true"
        :value="endDay"
        calendarButtonIcon="fa fa-calendar"
        :placeholder="dateToPlaceholder"
        :clearButton="clearButton"
        :disabled="!disabledDateRangeBySelect ? (disabledDate ? endDisabledDatesUntil : undefined) : endDisableBySeletedStartDate"
        :disabledPicker="disabled"
        :canPutTotop="datePickerCanPutTotop"
        @selected="updateEndDate"
      />
    </div>
    <div
      v-else-if="curInputType === INPUT_TYPE.RANGE"
      :class="['range-area', 'search-area', cls, disabled ? 'disabled-element': null]"
    >
      <HKLInput
        type="text"
        :value="startRange"
        :disabled="disabled"
        @input="updateStartRange($event.target.value)"
        @keyup.delete="updateStartRange($event.target.value)"
      />
      -
      <HKLInput
        type="text"
        :value="endRange"
        :disabled="disabled"
        @input="updateEndRange($event.target.value)"
        @keyup.delete="updateEndRange($event.target.value)"
      />
    </div>
    <template v-else-if="curInputType === INPUT_TYPE.COMPONENT">
      <component
        :is="component"
        :key="componentRenderKey"
        :class="{ 'search-filter-component--not-searchable': !componentSearchable }"
        :value="componentValue"
        :disabled="disabled"
        :showSearch="componentSearchable"
        v-bind="componentBindAttrs"
        v-on="getComponentListenersFn({ listeners: options.listeners })()"
        @change="returnMessage"
      />
    </template>
    <div v-else />
  </div>
</template>

<script>
import HKLInput from '@/controls/Input/HKLInput'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import DatePicker from '@/controls/DatePicker'
import componentMixin from '@/controls/componentMixin'
import * as regex from '@/utils/regex'
import { isEmpty, get } from 'lodash'
import i18n from '@/utils/i18n'
import { INPUT_TYPE } from './searchFilter.const'
import {
  hklValueToLegacyValue,
  isLegacyMultiSelectOptions,
  legacyOptionsToHklOptions,
  legacyValueToHklValue,
  normalizeEmptySelectValueToAll,
} from './searchFilterHklSelectAdapter'

export default {
  name: 'SearchFilter',
  components: {
    HKLInput,
    HKLSelect,
    HKLMultiSelect,
    DatePicker,
  },
  mixins: [componentMixin],
  props: {
    label: {
      type: String,
    },
    labelCls: {
      type: String,
      default: '',
    },
    inputType: {
      type: String,
      default: undefined,
    },
    type: {
      type: String,
      default: '',
    },
    secondtype: {
      type: String,
      default: '',
    },
    options: {
      type: [Array, Object],
      default: () => [],
    },
    groupValues: {
      type: String,
      default: '',
    },
    groupLabel: {
      type: String,
      default: '',
    },
    trackBy: {
      type: String,
      default: '',
    },
    customLabel: {
      type: Function,
      default: (option, label) => {
        if (isEmpty(option)) return ''
        return label ? option[label] : option
      },
    },
    onchange: {
      type: Function,
      default: () => {},
    },
    inputIcon: {
      type: String,
    },
    cls: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Array, Object],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    clearButton: {
      type: Boolean,
      default: true,
    },
    enterEvent: {
      type: Function,
      default: () => {},
    },
    dateFromPlaceholder: {
      type: String,
      default: () => i18n.t('dateFrom'),
    },
    dateToPlaceholder: {
      type: String,
      default: () => i18n.t('dateTo'),
    },
    multiLabel: {
      type: String,
      default: '',
    },
    singleDatePicker: {
      type: [Boolean, String],
      default: '',
    },
    searchable: {
      type: Boolean,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    disabledDate: {
      type: Boolean,
      default: true,
    },
    disabledDateRangeBySelect: {
      type: Boolean,
      default: false,
    },
    datePickerCanPutTotop: {
      type: Boolean,
      default: true,
    },
    normalizeEmptySelectToAll: {
      type: Boolean,
      default: false,
    },
    component: {
      type: Object,
      default: undefined,
    },
  },
  data () {
    const tips = {
      email: this.$i18n.t('hint.inputTip.email'),
      phone: this.$i18n.t('hint.inputTip.phone'),
      string: this.$i18n.t('hint.inputTip.string'),
    }
    return {
      lastChoose: '',
      formattedDate: ['', ''],
      tips,
      regex,
      startDay: null,
      endDay: null,
      formattedRange: ['', ''],
      startRange: null,
      endRange: null,
      startDisableBySelectedEndDate: {from: ''},
      endDisableBySeletedStartDate: {to: ''},
      INPUT_TYPE,
    }
  },
  computed: {
    endDisabledDatesUntil () {
      return {
        to: this.startDay,
        from: new Date(),
      }
    },
    startDisabledDatesUntil () {
      return {
        from: this.endDay && (new Date() > this.endDay) ? this.endDay : new Date(),
      }
    },
    curInputType () {
      if (this.component) return INPUT_TYPE.COMPONENT
      return this.inputType
    },
    selectSearchable () {
      return this.searchable !== false
    },
    componentSearchable () {
      if (typeof this.searchable === 'boolean') return this.searchable
      if (typeof this.componentBindAttrs.showSearch === 'boolean') return this.componentBindAttrs.showSearch
      const componentName = this.component && this.component.name
      return ['HKLSelect', 'HKLMultiSelect'].includes(componentName)
    },
    componentRenderKey () {
      return get(this.options, 'attributes.componentKey', this.type)
    },
    componentBindAttrs () {
      const attrs = this.getComponentAttrsFn({ attrs: this.options.attributes })() || {}
      if (attrs.componentKey != null) {
        const { componentKey, ...rest } = attrs
        return rest
      }
      return attrs
    },
    componentValue () {
      if (!this.normalizeEmptySelectToAll) return this.value
      return normalizeEmptySelectValueToAll(this.value, this.componentBindAttrs.options)
    },
    usesLegacySelectOptions () {
      return isLegacyMultiSelectOptions(this.options)
    },
    hklSelectOptions () {
      if (this.usesLegacySelectOptions) {
        return legacyOptionsToHklOptions(this.options, this.customLabel, this.multiLabel)
      }
      return this.options || []
    },
    hklSelectValue () {
      const value = legacyValueToHklValue(this.value, this.multiple, this.options, this.trackBy)
      if (!this.normalizeEmptySelectToAll || this.multiple) return value
      return normalizeEmptySelectValueToAll(value, this.hklSelectOptions)
    },
  },
  watch: {
    value: {
      handler: function (val, oldVal) {
        if (this.curInputType === INPUT_TYPE.DATE) {
          this.startDay = val && val[0]
          this.endDay = val && val[1]
          this.formattedDate = [this.startDay, this.endDay]
          this.startDisableBySelectedEndDate = { from: this.endDay || '' }
          this.endDisableBySeletedStartDate = { to: this.startDay || '' }
        } else if (this.curInputType === INPUT_TYPE.RANGE) {
          this.startRange = val && val[0]
          this.endRange = val && val[1]
          this.formattedRange = [this.startRange, this.endRange]
        }
      },
      deep: true,
    },
  },
  methods: {
    returnMessage (value) {
      const key = this.secondtype ? [this.type, this.secondtype].join('_') : this.type
      const val = { value: value, inputType: this.curInputType }
      const obj = {
        [key]: val,
      }
      this.onchange && this.onchange(obj)
    },
    onHklSelectChange (selectedValue) {
      const value = hklValueToLegacyValue(
        this.options,
        selectedValue,
        this.trackBy,
        this.multiple,
      )
      this.returnMessage(value)
    },
    updateStartDate (value) {
      this.formattedDate.splice(0, 1, value)
      this.returnMessage(this.formattedDate)
    },
    updateEndDate (value) {
      this.formattedDate.splice(1, 1, value)
      this.returnMessage(this.formattedDate)
    },
    updateStartRange (value) {
      this.formattedRange.splice(0, 1, value)
      this.returnMessage(this.formattedRange)
    },
    updateEndRange (value) {
      this.formattedRange.splice(1, 1, value)
      this.returnMessage(this.formattedRange)
    },
    _enterEvent (value) {
      this.returnMessage(value)
      this.enterEvent()
    },
  },
}
</script>

<style lang="scss" scoped>
$height: 40px;

.search-filter-input {
  display: inline-block;
  vertical-align: top;

  .search-title {
    font-size: 14px;
    font-weight: bold;
    height: 27px;
    line-height: 27px;
    display: inline-block;
  }
  .date-area.search-area {
    .vdp-datepicker{
      width: 50%;
      &.columns {
        float: left;
        &.singleDatePicker {
          width: 100%;
        }
      }
    }
  }
  .range-area input[type='text'] {
    width: calc(50% - 10px);
    display: inline-block;
  }
  .input-area input[type='text'] {
    &::-ms-clear {
      display: none;
    }
  }
  .search-area:not(.date-area):not(.select-area) {
    input {
      padding: 0;
      text-overflow: ellipsis;
    }
  }

  /* Disabled HKLSelect — select-area path and COMPONENT path (tenant-projects, projects, etc.) */
  & ::v-deep .ant-select-disabled.hkl-select {
    background: #ededed;
    opacity: 1;
    padding-right: 0;

    .ant-select-selection {
      background: #ededed;
      width: 100%;
      max-width: none;
    }

    .ant-select-selection__rendered,
    .ant-select-selection-selected-value,
    .ant-select-selection__placeholder {
      color: #0C3A58;
      opacity: 1;
    }
  }

  .search-filter-component--not-searchable ::v-deep .ant-select-selection--multiple .ant-select-search {
    display: none;
  }

.select-area {
  width: 100%;

  /* Legacy .multiselect min-width applies enabled and disabled */
  & ::v-deep .hkl-select {
    min-width: 100px;
    box-sizing: border-box;
  }

  & ::v-deep .hkl-select:not(.ant-select-disabled) {
    width: 100%;
    max-width: 100%;
    background: #fff;

    .ant-select-selection {
      background: none;
    }

    .ant-select-selection__choice {
      background: #6e7a91;
      height: 38px;
      margin-bottom: 0;
      line-height: 30px;
    }
  }

  /* Multi-select HKLSelect owns its arrow inside the bordered selection. */
  & ::v-deep .hkl-select[mode='multiple']:not(.ant-select-disabled) {
    padding-right: 0;

    .ant-select-selection--multiple {
      width: 100%;
    }
  }

  /* Single select — full control width, arrow at right edge (legacy .multiselect__select column) */
  & ::v-deep .hkl-select:not([mode='multiple']):not(.ant-select-disabled) {
    padding-right: 0;

    .ant-select-selection--single {
      width: 100%;
    }
  }

  /* Single HKLSelect alias used on contacts advance search (Company Status) */
  &.active ::v-deep .hkl-select:not(.ant-select-disabled) {
    padding-right: 0;

    .ant-select-selection--single {
      width: 100%;
    }
  }
}

  .input-area {
    width: 100%;
    height: $height;
    input {
      width: 100%;
      text-indent: 0.5rem;
    }
  }
  .input-area.disabled-element {
    background-color: #ededed;
    opacity: 1;
    input {
      background-color: #ededed;
      color: #0C3A58;
    }
  }
}

</style>
